import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_TYPES } from '@api/instant_survey';

import LogicOptions from '../LogicOptions';
import NumericLogicInput from '../NumericLogicInput';
import QuestionLogicOptions from '../QuestionLogicOptions';

import {
  answerOperators,
  availableLogicOptions,
  instantSurveyGroupRefUrlVariable,
  LOGIC_OPTIONS,
  numericLogicOperators,
} from '../../utilities/constants';
import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

const SkipLogicRow = ({
  firstErrorRef,
  index,
  isCompleted,
  questionId,
  questionIndex,
  questionList,
  questionLogic,
  setQuestionLogic,
}) => {
  const { survey } = useContext(SurveyContext);
  const getLogicType = () =>
    questionLogic[questionId].skip.logic[index]?.url_variable ===
      instantSurveyGroupRefUrlVariable || questionIndex === 0
      ? LOGIC_OPTIONS.QUOTA_GROUP.value
      : LOGIC_OPTIONS.ANSWER.value;

  const [logicType, setLogicType] = useState(getLogicType);

  useEffect(() => {
    setLogicType(getLogicType());
  }, [index, questionId, questionLogic, instantSurveyGroupRefUrlVariable]);

  const filteredQuestionList = questionList.filter(
    (question) => question.type !== QUESTION_TYPES.INSTRUCTIONS.value
  );
  const quotaGroupOptions = survey.groups
    .filter(({ id }) => id !== null && id !== undefined)
    .map((group) => ({
      id: group.id,
      text: group.name,
    }));
  const operatorOptions =
    questionLogic[questionId].skip.logic[index].question?.type === QUESTION_TYPES.NUMERIC.value
      ? numericLogicOperators
      : answerOperators;
  const isQuotaGroupSelectionValid = () => {
    const groupIds = quotaGroupOptions.map(({ id }) => String(id));
    const logicQuotaGroupIds = (questionLogic[questionId]?.skip?.logic?.[index]?.values || []).map(
      String
    );
    return logicQuotaGroupIds.every((id) => groupIds.includes(id));
  };
  const updateAnswers = (answers) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].skip.logic[index].answers = answers;
    updatedLogic[questionId].skip.logic[index].hasAnswerError = false;
    updatedLogic[questionId].skip.logic[index].isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateComparisonValue = (value) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].skip.logic[index].comparison_value = value;
    updatedLogic[questionId].skip.logic[index].hasAnswerError = false;
    updatedLogic[questionId].skip.logic[index].isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateLogicType = (newLogicType) => {
    setLogicType(newLogicType);
    const updatedLogic = cloneDeep(questionLogic);
    if (newLogicType === LOGIC_OPTIONS.QUOTA_GROUP.value) {
      updatedLogic[questionId].skip.logic[index] = {
        url_variable: instantSurveyGroupRefUrlVariable,
        values: [],
      };
    } else {
      updatedLogic[questionId].skip.logic[index] = {
        answers: [],
        question: null,
      };
    }
    updatedLogic[questionId].skip.logic[index].operator = answerOperators[0];
    updatedLogic[questionId].skip.logic[index].hasAnswerError = false;
    setQuestionLogic(updatedLogic);
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[questionId].skip.logic[index].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  const updateQuestion = (question) => {
    const updatedLogic = cloneDeep(questionLogic);
    delete updatedLogic[questionId].skip.logic[index].url_variable;
    delete updatedLogic[questionId].skip.logic[index].values;
    updatedLogic[questionId].skip.logic[index].answers = [];
    updatedLogic[questionId].skip.logic[index].question = question;
    updatedLogic[questionId].skip.logic[index].hasQuestionError = false;
    updatedLogic[questionId].skip.logic[index].isFirstQuestionError = false;

    if (question?.type === QUESTION_TYPES.NUMERIC.value) {
      updatedLogic[questionId].skip.logic[index].operator = numericLogicOperators[0];
    } else {
      updatedLogic[questionId].skip.logic[index].operator = answerOperators[0];
    }

    setQuestionLogic(updatedLogic);
  };

  const updateQuotaGroups = (groups) => {
    const updatedLogic = cloneDeep(questionLogic);
    const groupIds = groups.map(({ id }) => String(id));
    delete updatedLogic[questionId].skip.logic[index].answers;
    delete updatedLogic[questionId].skip.logic[index].question;
    delete updatedLogic[questionId].skip.logic[index].hasQuestionError;
    delete updatedLogic[questionId].skip.logic[index].isFirstQuestionError;
    updatedLogic[questionId].skip.logic[index].hasAnswerError = false;
    updatedLogic[questionId].skip.logic[index].url_variable = instantSurveyGroupRefUrlVariable;
    updatedLogic[questionId].skip.logic[index].values = groupIds;
    setQuestionLogic(updatedLogic);
  };
  return (
    <div className={styles['skip-logic-row']}>
      <span>
        The{' '}
        <LogicOptions
          isCompleted={isCompleted}
          logicOptions={
            questionIndex !== 0 ? availableLogicOptions : availableLogicOptions.slice(1)
          }
          optionsLabel="Type"
          setValue={updateLogicType}
          value={logicType}
        />
      </span>
      {logicType === LOGIC_OPTIONS.ANSWER.value ? (
        <>
          <span>&nbsp;to&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={questionLogic[questionId].skip.logic[index].hasQuestionError}
            isFocused={questionLogic[questionId].skip.logic[index].isFirstQuestionError}
            isQuestion
            isRequired
            options={filteredQuestionList}
            optionsLabel="Question"
            optionsType="radio"
            setValue={updateQuestion}
            value={questionLogic[questionId].skip.logic[index].question}
          />
          {questionLogic[questionId].skip.logic[index].question?.id ? (
            <>
              <span>&nbsp;is&nbsp;</span>
              <QuestionLogicOptions
                isCompleted={isCompleted}
                options={operatorOptions}
                optionsLabel="Operator"
                optionsType="radio"
                setValue={updateOperator}
                value={questionLogic[questionId].skip.logic[index].operator}
              />
              :
            </>
          ) : (
            <span>...</span>
          )}
          {questionLogic[questionId].skip.logic[index].question?.id && (
            <>
              {questionLogic[questionId].skip.logic[index].question.type ===
              QUESTION_TYPES.NUMERIC.value ? (
                <NumericLogicInput
                  firstErrorRef={firstErrorRef}
                  isCompleted={isCompleted}
                  isError={questionLogic[questionId].skip.logic[index].hasAnswerError}
                  isFocused={questionLogic[questionId].skip.logic[index].isFirstAnswerError}
                  isRequired
                  question={questionLogic[questionId].skip.logic[index].question}
                  setValue={updateComparisonValue}
                  value={
                    questionLogic[questionId].skip.logic[index].comparison_value ?? 'Numeric Value'
                  }
                />
              ) : (
                <QuestionLogicOptions
                  firstErrorRef={firstErrorRef}
                  isCompleted={isCompleted}
                  isError={questionLogic[questionId].skip.logic[index].hasAnswerError}
                  isFocused={questionLogic[questionId].skip.logic[index].isFirstAnswerError}
                  isRequired
                  isSkip
                  options={questionLogic[questionId].skip.logic[index].question?.answers}
                  optionsLabel="Answer Options"
                  optionsType="checkbox"
                  setValue={updateAnswers}
                  value={questionLogic[questionId].skip.logic[index].answers}
                />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <span>&nbsp;is&nbsp;</span>
          <QuestionLogicOptions
            isCompleted={isCompleted}
            options={answerOperators}
            optionsLabel="Operator"
            optionsType="radio"
            setValue={updateOperator}
            value={questionLogic[questionId].skip.logic[index].operator}
          />
          <span>&nbsp;:&nbsp;</span>
          <QuestionLogicOptions
            firstErrorRef={firstErrorRef}
            isCompleted={isCompleted}
            isError={
              !isQuotaGroupSelectionValid() ||
              questionLogic[questionId].skip.logic[index].hasAnswerError
            }
            isFocused={questionLogic[questionId].skip.logic[index].hasAnswerError}
            isQuotaGroup
            isQuotaGroupSelectionValid={isQuotaGroupSelectionValid()}
            isRequired
            options={quotaGroupOptions}
            optionsLabel="Quota Group"
            optionsType="checkbox"
            setValue={updateQuotaGroups}
            value={quotaGroupOptions.filter(({ id }) =>
              (questionLogic[questionId].skip.logic[index].values || []).includes(String(id))
            )}
          />
        </>
      )}
    </div>
  );
};

SkipLogicRow.propTypes = {
  firstErrorRef: PropTypes.object,
  index: PropTypes.number.isRequired,
  isCompleted: PropTypes.bool,
  questionId: PropTypes.number.isRequired,
  questionIndex: PropTypes.number,
  questionList: PropTypes.array.isRequired,
  questionLogic: PropTypes.object.isRequired,
  setQuestionLogic: PropTypes.func,
};

export default SkipLogicRow;
